import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import { RECEIPT_DETAIL_QUERY_KEY } from "./useReceipt";
import {
  ReceiptBucket,
  RECEIPTS_POLL_KEY,
  RECEIPTS_QUERY_KEY,
} from "./useReceiptsList";

import type { ReceiptDetail } from "./useReceipt";
import type { ReceiptListRow, ReceiptsListResponse } from "./useReceiptsList";

export interface UnlinkMatchError extends Error {
  isConflict: boolean;
  status?: number;
}

export interface UnlinkMatchVariables {
  suggestionId: number;
  receiptUuid: string;
}

export interface UseUnlinkMatchOptions {
  onSuccess?: (updated: ReceiptDetail) => void;
  onError?: (error: UnlinkMatchError) => void;
}

interface UnlinkMatchContext {
  prev: Array<[readonly unknown[], unknown]>;
}

// Optimistic-only fields: the list-row type doesn't yet model
// `no_match_reason`, but the matcher backend writes it and the Unmatched tab
// (FINT-254) will read it. Carrying it on the optimistic row keeps the
// Unmatched bucket's Reason copy correct between mutation success and
// invalidation refetch.
type OptimisticUnmatchedRow = ReceiptListRow & {
  no_match_reason?: "user_unlinked";
};

const transitionRowToUnmatched = (
  row: ReceiptListRow
): OptimisticUnmatchedRow => ({
  ...row,
  status: "no_match",
  suggested_match: null,
  no_match_reason: "user_unlinked",
});

const bumpCounts = (list: ReceiptsListResponse): ReceiptsListResponse => ({
  ...list,
  meta: {
    ...list.meta,
    counts: {
      ...list.meta.counts,
      matched: Math.max(0, list.meta.counts.matched - 1),
      unmatched: list.meta.counts.unmatched + 1,
    },
  },
});

const applyOptimisticUnlink = (
  list: ReceiptsListResponse,
  receiptUuid: string,
  bucket: unknown
): ReceiptsListResponse => {
  const idx = list.receipts.findIndex(
    (row: ReceiptListRow) => row.uuid === receiptUuid
  );
  // Row isn't cached for this bucket (e.g. the Unmatched tab hasn't been
  // visited yet). Skip mutation; onSettled invalidation will refetch and
  // surface the now-unlinked row in the right bucket.
  if (idx === -1) return list;

  if (bucket === ReceiptBucket.MATCHED) {
    // Drop the row from the Matched view so it doesn't render as broken
    // (status="no_match" with a matched-row layout) until refetch lands.
    return {
      ...bumpCounts(list),
      receipts: list.receipts.filter((row) => row.uuid !== receiptUuid),
    };
  }

  // Unfiltered "all" view: rewrite in place so the row reflects its new
  // unmatched status without disappearing.
  const next = [...list.receipts];
  next[idx] = transitionRowToUnmatched(next[idx]);
  return { ...bumpCounts(list), receipts: next };
};

const extractStatus = (err: unknown): number | undefined => {
  if (typeof err !== "object" || err === null) return undefined;
  const { response } = err as { response?: { status?: unknown } };
  if (!response || typeof response.status !== "number") return undefined;
  return response.status;
};

const extractMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Unlink match failed";
};

const toUnlinkMatchError = (err: unknown): UnlinkMatchError => {
  const status = extractStatus(err);
  const wrapped = new Error(extractMessage(err)) as UnlinkMatchError;
  wrapped.isConflict = status === 409;
  wrapped.status = status;
  return wrapped;
};

export const useUnlinkMatch = (options: UseUnlinkMatchOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation<
    ReceiptDetail,
    UnlinkMatchError,
    UnlinkMatchVariables,
    UnlinkMatchContext
  >({
    mutationFn: async ({ suggestionId }) => {
      try {
        const response = await ApiHttp.fetch<ReceiptDetail>(
          `receipt-transaction-suggestions/${suggestionId}/`,
          { method: "PATCH", rawResponse: true },
          { status: "unlinked" }
        );
        return (response as unknown as { data: ReceiptDetail }).data;
      } catch (err) {
        throw toUnlinkMatchError(err);
      }
    },
    onMutate: async ({ receiptUuid }) => {
      // Scope to bucket-list queries only — the upload-polling query
      // ([RECEIPTS_QUERY_KEY, "poll", uuid]) tracks its own visibility and
      // breaks if we mutate counts/rows out from under it.
      const isListQuery = (queryKey: readonly unknown[]): boolean =>
        queryKey[0] === RECEIPTS_QUERY_KEY && queryKey[1] !== RECEIPTS_POLL_KEY;
      await queryClient.cancelQueries({
        predicate: (q) => isListQuery(q.queryKey),
      });
      const prev = queryClient.getQueriesData({
        predicate: (q) => isListQuery(q.queryKey),
      });

      prev.forEach(([key, data]) => {
        if (!data) return;
        const next = applyOptimisticUnlink(
          data as ReceiptsListResponse,
          receiptUuid,
          key[1]
        );
        queryClient.setQueryData(key, next);
      });

      return { prev };
    },
    onError: (err, _vars, context) => {
      context?.prev.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      options.onError?.(err);
    },
    onSuccess: (updated) => {
      options.onSuccess?.(updated);
    },
    onSettled: (updated, _error, variables) => {
      queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === RECEIPTS_QUERY_KEY &&
          q.queryKey[1] !== RECEIPTS_POLL_KEY,
      });
      const detailUuid = updated?.uuid ?? variables.receiptUuid;
      if (detailUuid) {
        queryClient.invalidateQueries({
          queryKey: [RECEIPT_DETAIL_QUERY_KEY, detailUuid],
        });
      }
    },
  });
};
